import type { Locale } from "@/context/AppProviders";
import { SITE_CONTENT, type ModuleItem } from "@/content/site";
import {
  featurePath,
  getFeatureIndexFromSlug,
  getFeatureSlug,
  getServiceIdFromSlug,
  getServiceIndex,
  getServiceSlug,
  servicePath,
  slugifyFeature,
  type ServiceId,
} from "@/lib/routes";

export type { ServiceId };

export const SERVICE_IDS = [
  "ecommerce",
  "quickSale",
  "dealer",
  "accounting",
  "website",
] as const satisfies readonly ServiceId[];

export { slugifyFeature };

export function isServiceUrlSlug(locale: Locale, slug: string): slug is string {
  return getServiceIdFromSlug(locale, slug) !== null;
}

export type ServiceFeature = ModuleItem & {
  slug: string;
  href: string;
};

export type ServicePageData = {
  index: number;
  id: ServiceId;
  urlSlug: string;
  label: string;
  title: string;
  desc: string;
  badge?: string;
  features: ServiceFeature[];
};

export function getServiceByUrlSlug(locale: Locale, urlSlug: string): ServicePageData | null {
  const serviceId = getServiceIdFromSlug(locale, urlSlug);
  if (!serviceId) return null;

  const c = SITE_CONTENT[locale];
  const index = getServiceIndex(serviceId);
  const mod = c.mods[index];

  return {
    index,
    id: serviceId,
    urlSlug: getServiceSlug(locale, serviceId),
    label: c.modTabs[index],
    title: mod.title,
    desc: mod.desc,
    badge: mod.badge,
    features: mod.items.map((item, featureIndex) => {
      const featureSlug = getFeatureSlug(locale, serviceId, featureIndex);
      return {
        ...item,
        slug: featureSlug,
        href: featurePath(locale, serviceId, featureIndex),
      };
    }),
  };
}

export function resolveFeatureFromUrlSlug(
  locale: Locale,
  serviceUrlSlug: string,
  featureUrlSlug: string
): { service: ServicePageData; featureIndex: number } | null {
  const service = getServiceByUrlSlug(locale, serviceUrlSlug);
  if (!service) return null;

  const featureIndex = getFeatureIndexFromSlug(locale, service.id, featureUrlSlug);
  if (featureIndex === null) return null;

  return { service, featureIndex };
}

export function getServicesNavData(locale: Locale) {
  const overviewLabel = locale === "tr" ? "Genel Bakış" : "Overview";

  return SERVICE_IDS.map((id) => {
    const urlSlug = getServiceSlug(locale, id);
    const service = getServiceByUrlSlug(locale, urlSlug);
    if (!service) {
      return {
        id,
        label: "",
        href: servicePath(locale, id),
        overviewLabel,
        features: [] as { title: string; href: string }[],
      };
    }

    return {
      id,
      label: service.label,
      href: servicePath(locale, id),
      overviewLabel,
      features: service.features.map((feature) => ({
        title: feature.title,
        href: feature.href,
      })),
    };
  });
}

export { featurePath, getServiceIndex, servicePath };

export const SERVICE_NAV_ITEMS = SERVICE_IDS.map((id, index) => ({
  id,
  trLabel: SITE_CONTENT.tr.modTabs[index],
  enLabel: SITE_CONTENT.en.modTabs[index],
}));
